import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, SimpleChanges } from "@angular/core";
import { ToothStateTypeService } from "../../../services/toothStateType.service";
import { BOToothStateType } from "../../../models/odo/BOToothStateType.model";
import { BOToothStateDetail } from '../../../models/odo/BOToothStateDetail.model';

/**
 * INTERFAZ: Define la estructura del evento emitido
 */
export interface ToothStateChangeEvent {
  toothId: number;
  stateId: number;
  label: string;
  iconIndex: number;
}

/**
 * COMPONENTE: ToothStateIconComponent
 *
 * Responsabilidades:
 * - Renderizar el icono SVG del estado del diente
 * - Emitir eventos cuando el usuario hace click
 * - Mostrar/ocultar la línea verde de selección basado en la propiedad isSelected
 * - Mostrar tooltip con el nombre del estado
 *
 * Inputs:
 * - stateId: ID del estado del diente (1, 2, 3, 4, etc.)
 * - toothId: ID del diente (1-50)
 * - iconIndex: Índice del icono dentro del array items del diente
 * - isSelected: Booleano que indica si este icono está seleccionado
 *
 * Outputs:
 * - stateChange: Emite evento cuando el usuario hace click en el icono
 */
@Component({
  selector: 'app-tooth-state-icon',
  templateUrl: './tooth-state-icon.component.html',
  styleUrls: ['./tooth-state-icon.component.css'],
  //changeDetection: ChangeDetectionStrategy.OnPush // ✅ Optimización: solo actualizar cuando los inputs cambien
})
export class ToothStateIconComponent implements OnInit {

  /**
   * Input: ID del estado dental
   * Valores posibles: 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16
   * Se usa en *ngSwitchCase para renderizar el SVG correcto
   */
  @Input() stateId!: number;

  /**
   * Input: ID del diente
   * Valores: 1 a 50 (o cantidad de dientes en el odontograma)
   * Se envía en el evento stateChange para identificar qué diente fue modificado
   */
  @Input() toothId!: number;

  /**
   * Input: Índice del icono dentro del array items del diente
   * Ejemplo: si un diente tiene 5 estados, iconIndex puede ser 0, 1, 2, 3 o 4
   * Se usa para determinar si mostrar la línea verde
   */
  @Input() iconIndex: number = 0;

  /**
   * Input: Indica si este icono está actualmente seleccionado
   * Si es true, la línea verde aparece debajo del icono
   * El padre (PadreA) controla este valor basado en selectedIconsByTooth
   */
  @Input() isSelected: boolean = false;

  /**
   * Output: Evento emitido cuando el usuario hace click en el icono
   * Contiene información del diente, estado, etiqueta e índice del icono
   * El padre (PadreA) escucha este evento y actualiza selectedIconsByTooth
   */

  @Input() observations:string="";
  @Input() theItem: BOToothStateDetail=new BOToothStateDetail();
  @Output() stateChange = new EventEmitter<ToothStateChangeEvent>();

  /**
   * Ciclo de vida: Se ejecuta cuando el componente se inicializa
   * Útil para debug y validación de inputs
   */
  toothStateList: BOToothStateType[]=[];

  constructor(
                private _toothStateType     : ToothStateTypeService,
                private _cdr                : ChangeDetectorRef

  ) {}


  ngOnInit(): void
  {
    if (!this.stateId) {
      console.warn(`ToothStateIconComponent: stateId no definido para diente ${this.toothId}`);
    }
    console.log(
      `✅ ToothStateIconComponent inicializado -Diente: ${this.toothId}, Estado: ${this.stateId}, Índice: ${this.iconIndex}, Seleccionado: ${this.isSelected}, Observaciones:${this.observations}`
    );

    console.log('theItem completo:', this.theItem);
    this._toothStateType.getAll().subscribe({
      next:(data)=>
      {
        if(data) this.toothStateList=data;

      }
    })

  }
    ngOnChanges(changes: SimpleChanges): void {
    if (changes['theItem']) {
      console.log(`🔄 theItem actualizado - Diente:${this.toothId} Estado:${this.stateId}`,
                   this.theItem);
    }
  }
  /**
   * MÉTODO: emitStateChange
   *
   * Se ejecuta cuando el usuario hace click en el icono SVG
   * Emite un evento que el padre (PadreA) escucha para actualizar el estado global
   *
   * @param toothId - ID del diente
   * @param stateId - ID del estado
   * @param label - Nombre del estado (para mostrar en logs/UI)
   */
  emitStateChange(toothId: number, stateId: number, label: string): void {

    const event: ToothStateChangeEvent = {
      toothId,
      stateId,
      label,
      iconIndex: this.iconIndex,
    };

    console.log(`Estado cambiado:`, event);
    this.stateChange.emit(event);
  }

  /**
   * MÉTODO AUXILIAR: getStateLabel
   *
   * Retorna la etiqueta/nombre del estado basado en el ID
   * Útil para mostrar información adicional en el componente
   *
   * @param stateId - ID del estado
   * @returns Nombre del estado
   */
  getStateLabel(stateId: number): string {
    const stateLabels: { [key: number]: string } = {
      1: 'Sellante Indicado',
      2: 'Sellante Realizado',
      3: 'Caries',
      4: 'Endodoncia Realizada',
      5: 'Extracción Indicada',
      6: 'Extracción Realizada',
      7: 'Obturación',
      8: 'Corona',
      9: 'Implante',
      10: 'Ausente',
      11: 'Supernumerario',
      12: 'Traumatizado',
      13: 'Anomalía',
      14: 'Fluorosis',
      15: 'Manchado',
      16: 'Otro'
    };
    return stateLabels[stateId] || 'Estado Desconocido';
  }
}
